import type { TierBadge } from '../../../../modules/api';
import { TierSelector } from './TierSelector';

export interface PackInfoCardProps {
  title: string;
  buybackPercent?: number;
  tiers: TierBadge[];
  onTierSelect?: (poolOfferId: number) => void;
}

/**
 * Glass info card — Figma `17:5482` (desktop) / `31:14338` (mobile).
 *
 * Anatomy (top → bottom):
 *   • pt-[22px] · title centered (Inter Bold uppercase 20→24px, leading 28)
 *   • gap 8px   · subtitle centered (Inter 13/20 -0.1 tracking, 80% opacity)
 *                 with first "{N}% FMV buyback" bolded (Semi Bold)
 *   • gap 16px  · tier selector row, 14px side padding, 14px bottom padding
 *
 * Surface: 2px border, rounded 28px, backdrop-blur 27, double gradient,
 * outer drop-shadow. Wired to the same `--co-PackInfoCard-*` knobs as the
 * modern template so one Design Constructor entry paints both themes; the
 * inline CSS-var fallbacks reproduce the pre-knob classic baseline pixel
 * for pixel when no overrides are set.
 */
export function PackInfoCard({
  title,
  buybackPercent = 90,
  tiers,
  onTierSelect,
}: PackInfoCardProps) {
  return (
    <div
      data-co-component="PackInfoCard"
      // The classic baseline ramps title size 20px → 24px once the parent
      // container crosses 900px (the layout is container-query driven, not
      // viewport — sidebar may be narrower than the page). We park the
      // current resolved value on a local CSS var so the h2's
      // `fontSize` fallback chain can pick it up without losing the
      // container-query semantics. When the operator sets
      // `--co-PackInfoCard-titleSize` it overrides this anyway.
      className={
        'relative overflow-hidden [border-radius:var(--co-PackInfoCard-borderRadius,28px)] text-white ' +
        'shadow-[0_64px_64px_0_rgba(0,0,0,0.05)] ' +
        '[--classic-title-size:20px] @[900px]:[--classic-title-size:24px]'
      }
      // Same knob chain as modern PackInfoCard. CSS-var fallbacks below are
      // tuned to the legacy classic look (matches the previous GLASS_SURFACE
      // wash + 2px border) so an UNMODIFIED classic deployment renders
      // pixel-identical to the pre-knob version.
      style={{
        backgroundImage:
          'linear-gradient(' +
          'var(--co-PackInfoCard-bgDirection, 180deg), ' +
          'color-mix(in srgb, var(--co-PackInfoCard-bgFrom, rgba(255,255,255,0.20)) ' +
          'calc(var(--co-PackInfoCard-bgOpacity, 1) * 100%), transparent) 0%, ' +
          'color-mix(in srgb, var(--co-PackInfoCard-bgTo, rgba(153,153,153,0.20)) ' +
          'calc(var(--co-PackInfoCard-bgOpacity, 1) * 100%), transparent) 100%)',
        backdropFilter: 'blur(var(--co-PackInfoCard-glassBlur, 27px))',
        WebkitBackdropFilter: 'blur(var(--co-PackInfoCard-glassBlur, 27px))',
        borderStyle: 'solid',
        borderWidth: 'var(--co-PackInfoCard-borderWidth, 2px)',
        borderColor: 'var(--co-PackInfoCard-borderColor, rgba(171,174,179,0.24))',
      }}
    >
      {/* Title + subtitle */}
      <div className="flex flex-col items-center gap-2 pt-[22px] px-[14px]">
        <h2
          className="
            m-0 text-center uppercase font-bold
            leading-[28px] tracking-[-0.3px]
          "
          style={{
            color: 'var(--co-PackInfoCard-titleColor, #ffffff)',
            fontFamily: 'var(--co-PackInfoCard-fontFamily), Inter, system-ui, sans-serif',
            // Classic baseline: 20px on mobile, 24px ≥900px. The desktop
            // bump is preserved via `--classic-title-size` (set by the
            // root div's container-query classes). When the operator
            // sets `--co-PackInfoCard-titleSize` it takes precedence on
            // both breakpoints.
            fontSize:
              'var(--co-PackInfoCard-titleSize, var(--classic-title-size, 20px))',
          }}
        >
          {title}
        </h2>
        <p
          className="
            m-0 max-w-[309px] text-center
            leading-[20px] tracking-[-0.1px] font-normal
          "
          style={{
            color: 'var(--co-PackInfoCard-subtitleColor, rgba(255,255,255,0.8))',
            fontFamily: 'var(--co-PackInfoCard-fontFamily), Inter, system-ui, sans-serif',
            fontSize: 'var(--co-PackInfoCard-subtitleSize, 13px)',
          }}
        >
          <span className="font-semibold">{buybackPercent}% FMV buyback</span>
          {' '}available — or hold and redeem your vaulted graded card.
        </p>
      </div>

      {/* Tier row: 14px side + bottom padding, 16px top to subtitle */}
      <div className="pt-4 px-[14px] pb-[14px]">
        <TierSelector badges={tiers} {...(onTierSelect ? { onSelect: onTierSelect } : {})} />
      </div>
    </div>
  );
}
